import { onCleanup } from "solid-js"
import { createStore } from "solid-js/store"
import type { SessionTab, useTabs } from "@/context/tabs"
import type { CoworkSessionContext } from "./api"
import type { FollowupDraft } from "@/components/prompt-input/submit"

export type ReportTemplateRequest = {
  id: string
  templateID: string
  companyID: string
  projectID: string
  periodID: string | null
  sessionID: string
  visiblePrompt: string
  instructions: string
  reportOutputLanguage: string
}

export type ReportTemplateAction = {
  visiblePrompt: string
  instructions: string
  reportOutputLanguage: string
  workflowRunID: string
  expected?: Pick<ReportTemplateRequest, "companyID" | "projectID" | "periodID" | "sessionID">
  request?: ReportTemplateRequest
  status: "pending" | "claimed" | "failed" | "sent" | "queued"
  sending: boolean
  staged?: boolean
  restoreDraft?: boolean
}

export function createReportTemplateAction(
  input: Pick<ReportTemplateAction, "visiblePrompt" | "instructions" | "reportOutputLanguage" | "expected" | "request">,
): ReportTemplateAction {
  return {
    ...input,
    workflowRunID: crypto.randomUUID(),
    status: input.request ? "pending" : "claimed",
    sending: false,
    staged: false,
    restoreDraft: false,
  }
}

export function reportTemplateMemory(tabs: ReturnType<typeof useTabs>, tab: SessionTab) {
  return tabs.state(tab, "report-template-request", () => {
    const [state, setState] = createStore<{ action?: ReportTemplateAction; disposed: boolean }>({ disposed: false })
    onCleanup(() => setState("disposed", true))
    return { state, setState }
  })
}

export function queueReportTemplate(
  tabs: ReturnType<typeof useTabs>,
  tab: SessionTab,
  request: ReportTemplateRequest,
): void {
  const memory = reportTemplateMemory(tabs, tab)
  // A catalog handoff belongs to a fresh tab. Repeated beforeOpen calls cannot replace or replay it.
  if (memory.state.action || memory.state.disposed) return
  memory.setState(
    "action",
    createReportTemplateAction({
      visiblePrompt: request.visiblePrompt,
      instructions: request.instructions,
      reportOutputLanguage: request.reportOutputLanguage,
      expected: { ...request },
      request: { ...request },
    }),
  )
}

export function claimReportTemplate(memory: ReturnType<typeof reportTemplateMemory>) {
  const action = memory.state.action
  if (memory.state.disposed || action?.status !== "pending") return
  memory.setState("action", "status", "claimed")
  return memory.state.action
}

export function matchingReportTemplate(memory: ReturnType<typeof reportTemplateMemory>, draft: FollowupDraft) {
  const action = memory.state.action
  if (!action || action.status === "sent" || action.status === "queued") return
  const text = draft.prompt
    .map((part) => ("content" in part && !("synthetic" in part && part.synthetic) ? part.content : ""))
    .join("")
  return text === action.visiblePrompt ? action : undefined
}

export function advanceReportTemplate(
  memory: ReturnType<typeof reportTemplateMemory>,
  ready: {
    prompt: boolean
    loading: boolean
    model: boolean
    agent: boolean
    session: boolean
    blocked: boolean
    text: string
    expired?: boolean
  },
  callbacks: {
    stage: (text: string) => void
    run: (action: ReportTemplateAction) => void
    fail: (model: boolean) => void
  },
) {
  const action = memory.state.action
  if (memory.state.disposed || !action) return
  if (action.status === "failed" && action.restoreDraft && ready.prompt) {
    memory.setState("action", "restoreDraft", false)
    if (!ready.blocked && !ready.text) {
      memory.setState("action", "staged", true)
      callbacks.stage(action.visiblePrompt)
    }
    return
  }
  if (action.status !== "pending") return
  if (ready.expired) {
    memory.setState("action", { status: "failed", restoreDraft: !ready.prompt && !action.staged })
    callbacks.fail(false)
    return
  }
  if (!ready.prompt || ready.blocked) return
  if (!action.staged && !ready.text) {
    memory.setState("action", "staged", true)
    callbacks.stage(action.visiblePrompt)
  } else if (ready.text !== action.visiblePrompt) {
    claimReportTemplate(memory)
    memory.setState("action", "status", "failed")
    callbacks.fail(false)
    return
  }
  if (ready.loading) return
  const claimed = claimReportTemplate(memory)
  if (!claimed) return
  if (!ready.model || !ready.agent || !ready.session) {
    memory.setState("action", "status", "failed")
    callbacks.fail(!ready.model || !ready.agent)
    return
  }
  callbacks.run(claimed)
}

export function reportTemplateContextMatches(
  context: CoworkSessionContext | undefined,
  remoteSessionID: string,
  expected?: ReportTemplateAction["expected"],
) {
  return (
    !!context &&
    context.remote_opencode_session_id === remoteSessionID &&
    (!expected ||
      (context.company_id === expected.companyID &&
        context.project_id === expected.projectID &&
        (context.period_id ?? context.workflow_id ?? null) === expected.periodID &&
        context.session_id === expected.sessionID))
  )
}

export async function injectReportTemplateContext(input: {
  draft: FollowupDraft
  action?: ReportTemplateAction
  reportOutputLanguage: string
  getContext: (remoteSessionID: string, companyID?: string) => Promise<CoworkSessionContext | undefined>
  manifest: (projectID: string, periodID?: string, companyID?: string, strict?: boolean) => Promise<unknown[]>
  error: () => Error
}) {
  const { draft, action } = input
  const context = await input.getContext(draft.sessionID, action?.expected?.companyID)
  if (action && !reportTemplateContextMatches(context, draft.sessionID, action.expected)) throw input.error()
  if (!context) return draft
  const reportOutputLanguage =
    action?.reportOutputLanguage ?? draft.language?.reportOutputLanguage ?? input.reportOutputLanguage
  const workflow = context.session_metadata.workflow as { name?: string } | undefined
  const defaultAgentID =
    typeof context.session_metadata.default_agent_id === "string" ? context.session_metadata.default_agent_id : ""
  const defaultAgentName =
    typeof context.session_metadata.default_agent_name === "string" ? context.session_metadata.default_agent_name : ""
  const accountingChat =
    (context.session_metadata.agent as { template_id?: string } | undefined)?.template_id === "accounting" &&
    !context.session_metadata.workflow_run_id
  const files = action || accountingChat
    ? []
    : await input
        .manifest(context.project_id, context.period_id ?? context.workflow_id ?? undefined, context.company_id)
        .catch(() => [])
  const scope = [
    `company_id: ${context.company_id}`,
    `project_id: ${context.project_id}`,
    `workflow_id: ${context.workflow_id ?? ""}`,
    `workflow_run_id: ${action?.workflowRunID ?? crypto.randomUUID()}`,
    `workflow_name: ${workflow?.name ?? ""}`,
    `cowork_session_id: ${context.session_id}`,
    defaultAgentID ? `default_agent_id: ${defaultAgentID}` : undefined,
    defaultAgentName ? `default_agent_name: ${defaultAgentName}` : undefined,
    `report_output_language: ${reportOutputLanguage}`,
  ]
  const prefix = (action
    ? [
        "Andmera report execution context:",
        ...scope,
        action.instructions,
        "Load scoped files, company context, and Knowledge through Andmera tools using these IDs. Persist and verify only the selected report.",
      ]
    : [
        "Andmera context for this chat:",
        ...scope,
        context.accounting_profiles?.length
          ? `accounting_profiles: ${JSON.stringify(context.accounting_profiles)}`
          : undefined,
        "Report language instruction: When generating report artifacts or downloadable report files, use report_output_language unless the user's latest prompt explicitly asks for a different report language. This does not change reply_language.",
        ...(accountingChat
          ? ["Use andmera_platform_context and scoped file discovery for all operational files in this company/project/period's Files tab, including uploads from other chats. Older chat file selections do not restrict this scope. A bounded inventory is not the complete source set; discover remaining files when files_has_more is true. Keep any frozen report-run source scope binding."]
          : ["uploaded_files:", JSON.stringify(files), "raw_file_attachments: none. Use the uploaded_files manifest and file previews supplied in this context; do not expect uploaded files to appear as native chat attachments."]),
        "When saving transformations, extractions, artifacts, accounting analyses, posting suggestions, review items, or tax reports, use Cowork Agent API helper tools with this scoped context.",
        "Use the provided workflow_run_id for every save in this user request. Include it as workflow_run_id in extraction, transformation, report artifact, and accounting workflow payloads so retries update the same workflow run instead of creating duplicate workflow records.",
      ])
    .concat("")
    .filter(Boolean)
    .join("\n")
  return {
    ...draft,
    ...(draft.language
      ? {
          language: { ...draft.language, reportOutputLanguage },
        }
      : {}),
    prompt: [
      { type: "text" as const, content: prefix, start: 0, end: prefix.length, synthetic: true },
      ...draft.prompt,
    ],
  }
}
